import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Tooltip,
  Skeleton,
} from "@mui/material";
import {
  Search as SearchIcon,
  CasinoOutlined as LotteryIcon,
  RestartAlt as ResetIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Web3 & Services
import cinstance from "../../../Service/randomcinstance";
import usercinstance from "../../../Service/usercinstance";
import web3 from "../../../Service/web3";
import { colors, borderRadiusPx, customShadows } from "../../../../theme";
import { Path } from "../../../../static/Constants";

// Dashboard Layout & Shared UI
import { DashboardLayout } from "../layout";
import {
  CommonButton,
  CommonTabs,
  CommonSelect,
  CommonInput,
  CommonViewToggle,
} from "../../ui";

// Domain Lottery Components
import AdminLotteryCard, {
  LOTTERY_TYPES,
  LOTTERY_SORT_OPTIONS,
  lotteryTypeMapper,
  resultStatusTypeMapper,
  safeFromWei,
} from "./AdminLotteryCard";
import AdminLotteryTable from "./AdminLotteryTable";
import LotteryStatsCards from "./LotteryStatsCards";

/**
 * Unified LotteryListPage Component
 * Handles both Admin (Platform Overview) and Creator (My Lotteries) modes via role condition.
 */
const LotteryListPage = ({ role }) => {
  const history = useHistory();
  const location = useLocation();

  // Determine active role: 'admin' | 'creator'
  const resolvedRole =
    role || (location.pathname.startsWith("/admin") ? "admin" : "creator");
  const isAdmin = resolvedRole === "admin";

  // Authentication & Access State
  const [account, setAccount] = useState("");
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [creatorInfo, setCreatorInfo] = useState({
    name: "Creator",
    active: true,
  });

  // Data State
  const [lotteries, setLotteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters & Search State
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'table'

  /**
   * Fetch lotteries from smart contracts based on role
   */
  const fetchLotteries = useCallback(async () => {
    try {
      if (!window.ethereum && !web3) {
        setLoading(false);
        return;
      }

      window.web3 = web3;
      let accounts = [];
      try {
        if (window.ethereum?.request) {
          accounts = await window.ethereum.request({ method: "eth_accounts" });
        }
        if (!accounts || accounts.length === 0) {
          accounts = await web3.eth.getAccounts();
        }
      } catch (err) {
        console.warn("Could not retrieve accounts", err);
      }

      const userAccount = accounts && accounts.length > 0 ? accounts[0] : "";
      setAccount(userAccount);

      if (!userAccount) {
        setLoading(false);
        setAuthChecked(true);
        return;
      }

      let idsToFetch = [];

      if (isAdmin) {
        // -------------------------------------------------------------
        // Admin Access Guard & Data Resolution
        // -------------------------------------------------------------
        let contractAdmin = "";
        try {
          contractAdmin = await cinstance.methods.admin().call();
        } catch (err) {
          console.warn("Could not get contract admin", err);
        }

        const authorized = Boolean(
          contractAdmin &&
            userAccount &&
            contractAdmin.toLowerCase() === userAccount.toLowerCase(),
        );
        setIsAuthorized(authorized);
        setAuthChecked(true);

        if (!authorized) {
          setLoading(false);
          return;
        }

        // Fetch Total Lottery Count
        let totalCount = 0;
        try {
          totalCount = await cinstance.methods.lotteryId().call();
        } catch (countErr) {
          console.warn("Could not fetch lotteryId count", countErr);
        }

        const count = Number(totalCount);
        if (count > 1) {
          for (let i = count - 1; i > 0; i--) {
            idsToFetch.push(i);
          }
        }
      } else {
        // -------------------------------------------------------------
        // Creator Access Guard & Data Resolution
        // -------------------------------------------------------------
        try {
          const orgData = await usercinstance.methods
            .organisationbyaddr(userAccount)
            .call();
          setCreatorInfo(orgData || { name: "Creator", active: true });
          setIsAuthorized(Boolean(orgData ? orgData.active : true));
        } catch (orgErr) {
          console.warn("Could not fetch creator org info", orgErr);
          setCreatorInfo({ name: "Creator", active: true });
          setIsAuthorized(true);
        }

        setAuthChecked(true);

        // Fetch Creator's lotteries
        let orgLotteryIds = [];
        try {
          orgLotteryIds = await cinstance.methods
            .getOrglotteries(userAccount)
            .call();
        } catch (idErr) {
          console.warn("Could not fetch org lotteries list:", idErr);
        }

        if (orgLotteryIds && orgLotteryIds.length > 0) {
          idsToFetch = [...orgLotteryIds].reverse(); // newest first
        }
      }

      if (idsToFetch.length === 0) {
        setLotteries([]);
        setLoading(false);
        return;
      }

      // Load lotteries in parallel batches to prevent RPC rate-limiting
      const validLotteries = [];
      const batchSize = 8;

      for (let i = 0; i < idsToFetch.length; i += batchSize) {
        const batchIds = idsToFetch.slice(i, i + batchSize);
        const batchResults = await Promise.all(
          batchIds.map(async (id) => {
            try {
              const details = await cinstance.methods.lottery(id).call();
              let dates = {};
              try {
                dates = await cinstance.methods.lotteryDates(id).call();
              } catch (dateErr) {
                // Non-fatal if dates cannot be fetched for this specific id
              }

              return {
                ...details,
                lotteryId: id,
                startTime: dates.startTime || details.deployedOn,
                drawTime:
                  dates.drawTime || dates.rollDate || details.deployedOn,
                rolloverdays: dates.rolloverdays || details.rolloverdays || 0,
              };
            } catch (lotteryErr) {
              console.warn(`Failed to fetch lottery #${id}`, lotteryErr);
              return null;
            }
          }),
        );
        validLotteries.push(...batchResults.filter(Boolean));
      }

      setLotteries(validLotteries);
    } catch (error) {
      console.error("Error fetching lotteries:", error);
      toast.error("Failed to load lotteries from smart contract.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchLotteries();

    if (window.ethereum?.on) {
      const handleAccountsChanged = (newAccounts) => {
        if (newAccounts && newAccounts.length > 0) {
          setAccount(newAccounts[0]);
        }
        fetchLotteries();
      };
      const handleChainChanged = () => {
        fetchLotteries();
      };

      window.ethereum.on("accountsChanged", handleAccountsChanged);
      window.ethereum.on("chainChanged", handleChainChanged);

      return () => {
        if (window.ethereum?.removeListener) {
          window.ethereum.removeListener(
            "accountsChanged",
            handleAccountsChanged,
          );
          window.ethereum.removeListener("chainChanged", handleChainChanged);
        }
      };
    }
  }, [fetchLotteries]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLotteries();
    toast.success("Lotteries data refreshed!");
  };

  /**
   * Shareable link handler
   */
  const handleShare = (lottery) => {
    const url = `${window.location.origin}${Path.getLotteryDetails(
      lottery.lotteryId,
    )}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      toast.success(`Shareable link copied for Lottery #${lottery.lotteryId}!`);
    }
  };

  /**
   * Navigate to details based on active role
   */
  const handleViewDetails = (lotteryId) => {
    history.push(
      isAdmin
        ? Path.getAdminLotteryDetails(lotteryId)
        : Path.getCreatorLotteryDetails(lotteryId),
    );
  };

  /**
   * Filter and Sort Logic
   */
  const filteredLotteries = useMemo(() => {
    return lotteries
      .filter((item) => {
        // Status Tab Filtering
        if (activeTab === "open") {
          if (
            item.status !== "0" &&
            item.status !== 0 &&
            item.status !== "5" &&
            item.status !== 5
          ) {
            return false;
          }
        } else if (activeTab === "closed") {
          if (
            item.status !== "1" &&
            item.status !== 1 &&
            item.status !== "2" &&
            item.status !== 2
          ) {
            return false;
          }
        } else if (activeTab === "rollover") {
          if (
            item.status !== "3" &&
            item.status !== 3 &&
            item.status !== "4" &&
            item.status !== 4
          ) {
            return false;
          }
        } else if (activeTab === "progress") {
          if (item.status !== "6" && item.status !== 6) {
            return false;
          }
        }

        // Game Type Filtering
        if (selectedType !== "all") {
          if (String(item.lotteryType) !== String(selectedType)) {
            return false;
          }
        }

        // Search Query Filtering
        if (searchQuery.trim() !== "") {
          const query = searchQuery.toLowerCase().trim();
          const idMatch = String(item.lotteryId).includes(query);
          const creatorMatch = item.ownerAddress?.toLowerCase().includes(query);
          const typeName = (
            lotteryTypeMapper[item.lotteryType] ||
            LOTTERY_TYPES[item.lotteryType]?.name ||
            ""
          ).toLowerCase();
          const shortTypeName = (
            LOTTERY_TYPES[item.lotteryType]?.shortName || ""
          ).toLowerCase();
          const statusName = (
            resultStatusTypeMapper[item.status] || ""
          ).toLowerCase();
          const typeMatch =
            typeName.includes(query) ||
            shortTypeName.includes(query) ||
            statusName.includes(query);
          const prizeMatch = safeFromWei(item.totalPrize).includes(query);

          if (!idMatch && !creatorMatch && !typeMatch && !prizeMatch) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        if (sortBy === "newest") {
          return Number(b.lotteryId) - Number(a.lotteryId);
        }
        if (sortBy === "oldest") {
          return Number(a.lotteryId) - Number(b.lotteryId);
        }
        if (sortBy === "highestPrize") {
          const prizeA = parseFloat(safeFromWei(a.totalPrize)) || 0;
          const prizeB = parseFloat(safeFromWei(b.totalPrize)) || 0;
          return prizeB - prizeA;
        }
        if (sortBy === "lowestPrize") {
          const prizeA = parseFloat(safeFromWei(a.totalPrize)) || 0;
          const prizeB = parseFloat(safeFromWei(b.totalPrize)) || 0;
          return prizeA - prizeB;
        }
        if (sortBy === "entryFee") {
          const feeA = parseFloat(safeFromWei(a.entryFee)) || 0;
          const feeB = parseFloat(safeFromWei(b.entryFee)) || 0;
          return feeB - feeA;
        }
        return 0;
      });
  }, [lotteries, activeTab, selectedType, searchQuery, sortBy]);

  // Tab Badge Counts
  const tabCounts = useMemo(() => {
    return {
      all: lotteries.length,
      open: lotteries.filter(
        (l) =>
          l.status === "0" ||
          l.status === 0 ||
          l.status === "5" ||
          l.status === 5,
      ).length,
      closed: lotteries.filter(
        (l) =>
          l.status === "1" ||
          l.status === 1 ||
          l.status === "2" ||
          l.status === 2,
      ).length,
      rollover: lotteries.filter(
        (l) =>
          l.status === "3" ||
          l.status === 3 ||
          l.status === "4" ||
          l.status === 4,
      ).length,
      progress: lotteries.filter((l) => l.status === "6" || l.status === 6)
        .length,
    };
  }, [lotteries]);

  const tabItems = useMemo(
    () => [
      {
        value: "all",
        label: "All Lotteries",
        count: tabCounts.all,
        chipVariant: "neutral",
      },
      {
        value: "open",
        label: "Open Lotteries",
        count: tabCounts.open,
        chipVariant: "active",
        chipBg: colors.emerald[50],
        chipColor: colors.emerald[700],
      },
      {
        value: "closed",
        label: "Closed Lotteries",
        count: tabCounts.closed,
        chipVariant: "ended",
      },
      {
        value: "rollover",
        label: "Rollover Lotteries",
        count: tabCounts.rollover,
        chipVariant: "warning",
        chipBg: colors.amber[50],
        chipColor: colors.amber[700],
      },
      {
        value: "progress",
        label: "In Progress",
        count: tabCounts.progress,
        chipVariant: "pending",
        chipBg: colors.purple ? colors.purple[50] : "#f3e8ff",
        chipColor: colors.purple ? colors.purple[700] : "#7e22ce",
      },
    ],
    [tabCounts],
  );

  const handleResetFilters = () => {
    setActiveTab("all");
    setSelectedType("all");
    setSearchQuery("");
    setSortBy("newest");
  };

  return (
    <DashboardLayout
      role={resolvedRole}
      title={isAdmin ? "Lotteries Management" : "My Lotteries"}
      subtitle={
        isAdmin
          ? "Monitor, filter, and inspect all smart contract lotteries and draws across the AutoBET platform."
          : "Manage, track, and inspect all decentralized lotteries deployed by your creator organisation."
      }
      breadcrumbs={[
        {
          label: "Dashboard",
          href: isAdmin ? Path.admin : Path.org,
        },
        { label: isAdmin ? "Lotteries List" : "My Lotteries" },
      ]}
      headerActions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {!isAdmin && (
            <CommonButton
              variant="contained"
              onClick={() => history.push(Path.orgcreatelottery)}
              startIcon={<AddIcon />}
            >
              Create Lottery
            </CommonButton>
          )}
          <Tooltip title="Refresh lottery data from contract" arrow>
            <CommonButton
              variant="refresh"
              onClick={handleRefresh}
              loading={loading || refreshing}
            />
          </Tooltip>
        </Box>
      }
      creatorAddress={account}
      userName={isAdmin ? "AutoBET Admin" : creatorInfo?.name || "Creator"}
      userRole={isAdmin ? "Administrator" : "Creator"}
      authChecked={authChecked}
      isAuthorized={isAuthorized}
      accessDeniedProps={{
        title: isAdmin ? "Access Denied" : "Account Access Denied",
        message: isAdmin
          ? "You must be an administrator to view or manage platform lotteries."
          : "You must be a registered Creator or organization to view My Lotteries.",
        actionText: isAdmin ? "Return to Home" : "Register as Organisation",
        actionPath: isAdmin ? Path.root : Path.registerasorg,
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* KPI Metrics Ribbon */}
      <Box>
        <LotteryStatsCards lotteries={lotteries} />
      </Box>

      {/* Filter Toolbar & View Switcher */}
      <Box
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: colors.background.paper,
          borderRadius: borderRadiusPx.xl,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardSubtle,
        }}
      >
        {/* Status Tabs Navigation */}
        <Box
          sx={{
            borderBottom: `1px solid ${colors.dashboard.divider}`,
            mb: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <CommonTabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            items={tabItems}
          />

          {/* View Mode Toggle: Grid vs Table */}
          <CommonViewToggle value={viewMode} onChange={setViewMode} />
        </Box>

        {/* Filtering Controls Row */}
        <Box>
          <Grid container spacing={2} alignItems="center">
            {/* Search Input */}
            <Grid item xs={12} md={5} lg={5}>
              <CommonInput
                fullWidth
                placeholder={
                  isAdmin
                    ? "Search by ID, creator address, game type, or prize..."
                    : "Search by ID, game type, or prize pool..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                startAdornment={
                  <SearchIcon
                    sx={{ fontSize: 18, color: colors.neutral.lightGrey }}
                  />
                }
              />
            </Grid>

            {/* Game Type Filter */}
            <Grid item xs={6} sm={4} md={2.5} lg={2.5}>
              <CommonSelect
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                options={[
                  { value: "all", label: "All Game Types" },
                  ...Object.entries(lotteryTypeMapper).map(
                    ([typeId, typeName]) => ({
                      value: String(typeId),
                      label: typeName,
                    }),
                  ),
                ]}
                size="small"
              />
            </Grid>

            {/* Sort By Filter */}
            <Grid item xs={6} sm={4} md={2.5} lg={2.5}>
              <CommonSelect
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                options={LOTTERY_SORT_OPTIONS}
                size="small"
              />
            </Grid>

            {/* Reset Filters */}
            <Grid item xs={12} sm={4} md={2} lg={2}>
              <CommonButton
                variant="reset"
                fullWidth
                onClick={handleResetFilters}
                startIcon={<ResetIcon sx={{ fontSize: 18 }} />}
              >
                Reset
              </CommonButton>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Content Display: Loading / Empty / Grid / Table */}
      {loading ? (
        <Box>
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: borderRadiusPx.xl,
                    bgcolor: colors.background.paper,
                    border: `1px solid ${colors.dashboard.cardBorder}`,
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    height={30}
                    sx={{ mb: 2, borderRadius: borderRadiusPx.sm }}
                  />
                  <Skeleton variant="text" height={24} sx={{ mb: 1 }} />
                  <Skeleton
                    variant="rectangular"
                    height={70}
                    sx={{ mb: 2, borderRadius: borderRadiusPx.md }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={100}
                    sx={{ mb: 2, borderRadius: borderRadiusPx.md }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={36}
                    sx={{ borderRadius: borderRadiusPx.md }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : filteredLotteries.length === 0 ? (
        <Box
          sx={{
            p: 6,
            textAlign: "center",
            bgcolor: colors.background.paper,
            borderRadius: borderRadiusPx.xl,
            border: `1px dashed ${colors.neutral.border}`,
          }}
        >
          <LotteryIcon
            sx={{ fontSize: 48, color: colors.slate[300], mb: 1.5 }}
          />
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: colors.slate[800], mb: 0.5 }}
          >
            No Lotteries Found
          </Typography>
          <Typography
            sx={{
              color: colors.slate[500],
              fontSize: "14px",
              mb: 2.5,
              maxWidth: "420px",
              mx: "auto",
            }}
          >
            {searchQuery || selectedType !== "all" || activeTab !== "all"
              ? "No smart contract lotteries matched your active filters or search keywords."
              : isAdmin
              ? "There are currently no lotteries created on the smart contract."
              : "You haven't deployed any smart contract lotteries yet."}
          </Typography>
          {searchQuery || selectedType !== "all" || activeTab !== "all" ? (
            <CommonButton
              variant="primary"
              onClick={handleResetFilters}
              startIcon={<ResetIcon />}
            >
              Reset All Filters
            </CommonButton>
          ) : !isAdmin ? (
            <CommonButton
              variant="primary"
              onClick={() => history.push(Path.orgcreatelottery)}
              startIcon={<AddIcon />}
            >
              Create Your First Lottery
            </CommonButton>
          ) : null}
        </Box>
      ) : viewMode === "grid" ? (
        <Box>
          <Grid container spacing={3}>
            {filteredLotteries.map((item) => (
              <Grid item xs={12} sm={6} md={6} lg={4} key={item.lotteryId}>
                <AdminLotteryCard
                  lottery={item}
                  onViewDetails={handleViewDetails}
                  onShare={handleShare}
                  userRole={resolvedRole}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <AdminLotteryTable
          lotteries={filteredLotteries}
          onViewDetails={handleViewDetails}
          onShare={handleShare}
          userRole={resolvedRole}
        />
      )}
    </DashboardLayout>
  );
};

LotteryListPage.propTypes = {
  role: PropTypes.oneOf(["admin", "creator"]),
};

export default LotteryListPage;
